import { type ComponentProps, useRef } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { LI } from '@expo/html-elements';
import { DecorativeText, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsTabLink = (props: Props) => {
  const { title, active, nativeID, panelNativeID, ...restProps } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const pressableRef = useRef(null);

  // ─── Styles ──────────────────────────────────────────────────────────

  const isHovered = useHovered(pressableRef);

  const containerStyles = [
    pressableWebStyles,
    styles.container,
    active || isHovered ? styles.containerActive : undefined,
  ];
  const titleStyles = [styles.title, active ? styles.titleActive : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LI>
      <Pressable
        ref={pressableRef}
        accessibilityLabel={title}
        style={containerStyles}
        nativeID={nativeID}
        accessibilityRole="tab"
        aria-selected={active}
        {...{ accessibilityControls: panelNativeID }}
        {...restProps}
      >
        <DecorativeText sizeMatch={['22']} style={titleStyles}>
          {title}
        </DecorativeText>
      </Pressable>
    </LI>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    userSelect: 'none',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.TRANSPARENT,
    paddingBottom: 2,
  },
  containerActive: {
    borderBottomColor: theme.colors.KALE,
  },
  title: {
    color: theme.colors.CHARCOAL,
  },
  titleActive: {
    color: theme.colors.KALE,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  title: string;
  active: boolean;
  nativeID: string;
  panelNativeID: string;
} & Omit<
  ComponentProps<typeof Pressable>,
  'children' | 'accessibilityLabel' | 'nativeID'
>;
